import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@utils/cn'

const Navbar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50)
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const navItems = [
        { id: 'inicio', label: 'Inicio', href: '#inicio' },
        { id: 'servicios', label: 'Servicios', href: '#servicios' },
        { id: 'nosotros', label: 'Nosotros', href: '#nosotros' },
        { id: 'contacto', label: 'Contacto', href: '#contacto' },
    ]

    const handleNavClick = (href: string) => {
        setIsOpen(false)
        const element = document.querySelector(href)
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
        }
    }

    return (
        <motion.nav
            className={cn(
                'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
                isScrolled
                    ? 'glass-navbar shadow-lg backdrop-blur-lg'
                    : 'bg-transparent'
            )}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            <div className="container-fluid">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <motion.div
                        className="flex items-center space-x-2"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="flex flex-col">
                            <span className="text-base sm:text-lg md:text-xl font-black text-gray-900 leading-none">
                                Grupo Médico
                            </span>
                            <span className="text-sm sm:text-base md:text-lg font-bold text-primary-600 leading-none">
                                Empresarial
                            </span>
                        </div>
                    </motion.div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {navItems.map((item) => (
                            <motion.button
                                key={item.id}
                                onClick={() => handleNavClick(item.href)}
                                className={cn(
                                    'text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200',
                                    'relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5',
                                    'after:bg-primary-600 after:transition-all after:duration-300',
                                    'hover:after:w-full focus:outline-none focus:after:w-full'
                                )}
                                whileHover={{ y: -2 }}
                                whileTap={{ y: 0 }}
                            >
                                {item.label}
                            </motion.button>
                        ))}

                        {/* CTA Button */}
                        <motion.button
                            onClick={() => handleNavClick('#contacto')}
                            className="btn-primary text-sm px-6 py-2"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Agendar Cita
                        </motion.button>
                    </div>

                    {/* Mobile Menu Button */}
                    <motion.button
                        className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => setIsOpen(!isOpen)}
                        whileTap={{ scale: 0.95 }}
                        aria-label="Toggle menu"
                    >
                        <div className="w-6 h-6 relative">
                            <motion.span
                                className={cn(
                                    'absolute block w-full h-0.5 bg-gray-700 transform transition-all duration-300',
                                    isOpen ? 'rotate-45 top-3' : 'top-1'
                                )}
                            />
                            <motion.span
                                className={cn(
                                    'absolute block w-full h-0.5 bg-gray-700 top-3 transition-all duration-300',
                                    isOpen ? 'opacity-0' : 'opacity-100'
                                )}
                            />
                            <motion.span
                                className={cn(
                                    'absolute block w-full h-0.5 bg-gray-700 transform transition-all duration-300',
                                    isOpen ? '-rotate-45 top-3' : 'top-5'
                                )}
                            />
                        </div>
                    </motion.button>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="md:hidden glass-navbar border-t border-white/20"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="container-fluid py-4">
                            <div className="flex flex-col space-y-4">
                                {navItems.map((item, index) => (
                                    <motion.button
                                        key={item.id}
                                        onClick={() => handleNavClick(item.href)}
                                        className="text-left text-gray-700 hover:text-primary-600 font-medium py-2 transition-colors duration-200"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        {item.label}
                                    </motion.button>
                                ))}

                                <motion.button
                                    onClick={() => handleNavClick('#contacto')}
                                    className="btn-primary text-sm py-3 mt-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    Agendar Cita
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    )
}

export default Navbar